import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Cell } from 'react-md/lib/Grids';
import { ExpansionList, ExpansionPanel } from 'react-md/lib/ExpansionPanels';
import { Button } from 'react-md/lib/Buttons';
import Card, { CardTitle } from 'react-md/lib/Cards';
import { FontIcon } from 'react-md/lib/FontIcons';
import { DialogContainer } from 'react-md/lib/Dialogs';
import {
   IdentityActions,
   selectMfaResetResponse,
   selectMfaDetails,
   selectMfaSmsEnrollmentResponse,
   selectMfaAuthenticatorEnrollmentResponse,
   selectMfaDefaultMethodResponse,
} from 'State/Redux/IdentityRedux';
import { selectContact } from 'State/Redux/ContactRedux';
import { FN_EMPTY_VOID } from 'Util/Helpers/Empty';

type PendingAction = 'remove-sms' | 'remove-authenticator' | 'reset-mfa' | null;

const DIALOG_TITLES: Record<NonNullable<PendingAction>, string> = {
   'remove-sms': 'Remove SMS Enrollment',
   'remove-authenticator': 'Remove Authenticator Enrollment',
   'reset-mfa': 'Reset MFA',
};

const DIALOG_MESSAGES: Record<NonNullable<PendingAction>, string> = {
   'remove-sms':
      'Are you sure you want to remove SMS enrollment for this user?',
   'remove-authenticator':
      'Are you sure you want to remove Authenticator enrollment for this user?',
   'reset-mfa':
      'Are you sure you want to reset MFA for this user? This will remove all enrolled methods and require re-enrollment at next login.',
};

const MfaTab = (): JSX.Element => {
   const contact = useSelector(selectContact);
   const mfaResetResponse = useSelector(selectMfaResetResponse);
   const mfaDetails = useSelector(selectMfaDetails);
   const smsEnrollmentResponse = useSelector(selectMfaSmsEnrollmentResponse);
   const authenticatorEnrollmentResponse = useSelector(
      selectMfaAuthenticatorEnrollmentResponse
   );
   const defaultMethodResponse = useSelector(selectMfaDefaultMethodResponse);
   const dispatch = useDispatch();

   const [notice, setNotice] = useState<string | null>(null);
   const [pendingAction, setPendingAction] = useState<PendingAction>(null);
   const [showDialog, setShowDialog] = useState(false);

   const openDialog = (action: NonNullable<PendingAction>): void => {
      setPendingAction(action);
      setShowDialog(true);
   };

   const closeDialog = (): void => {
      setShowDialog(false);
      setPendingAction(null);
   };

   const handleConfirm = (): void => {
      switch (pendingAction) {
         case 'remove-sms':
            dispatch(
               IdentityActions.deleteMfaSmsEnrollmentRequest(contact.userId)
            );
            break;
         case 'remove-authenticator':
            dispatch(
               IdentityActions.deleteMfaAuthenticatorEnrollmentRequest(
                  contact.userId
               )
            );
            break;
         case 'reset-mfa':
            dispatch(IdentityActions.postMfaResetRequest(contact.userId));
            break;
         default:
            break;
      }
      closeDialog();
   };

   const refreshDetails = useCallback((): void => {
      if (contact.userId) {
         dispatch(IdentityActions.getMfaDetailsRequest(contact.userId));
      }
   }, [contact.userId, dispatch]);

   useEffect(refreshDetails, [refreshDetails]);

   useEffect((): void => {
      if (mfaResetResponse.success) {
         setNotice('MFA has been reset successfully.');
         refreshDetails();
      }
      dispatch(IdentityActions.mfaResetResponseReset());
   }, [mfaResetResponse, dispatch, refreshDetails]);

   useEffect((): void => {
      if (smsEnrollmentResponse.success) {
         setNotice('SMS enrollment has been removed.');
         refreshDetails();
      }
      dispatch(IdentityActions.mfaSmsEnrollmentResponseReset());
   }, [smsEnrollmentResponse, dispatch, refreshDetails]);

   useEffect((): void => {
      if (authenticatorEnrollmentResponse.success) {
         setNotice('Authenticator enrollment has been removed.');
         refreshDetails();
      }
      dispatch(IdentityActions.mfaAuthenticatorEnrollmentResponseReset());
   }, [authenticatorEnrollmentResponse, dispatch, refreshDetails]);

   useEffect((): void => {
      if (defaultMethodResponse.success) {
         setNotice('Default MFA method has been updated.');
         refreshDetails();
      }
      dispatch(IdentityActions.mfaDefaultMethodResponseReset());
   }, [defaultMethodResponse, dispatch, refreshDetails]);

   const bothEnrolled = !!(
      mfaDetails?.totpEnrolledDate && mfaDetails?.smsEnrolledDate
   );
   const resetDisabled =
      !mfaDetails?.smsEnrolledDate && !mfaDetails?.totpEnrolledDate;
   const smsDefaultDisabled =
      !bothEnrolled || mfaDetails?.preferredMfaMethod === 'SMS';
   const authenticatorDefaultDisabled =
      !bothEnrolled || mfaDetails?.preferredMfaMethod === 'AUTHENTICATOR';
   const smsRemoveDisabled = !bothEnrolled;
   const authenticatorRemoveDisabled = !bothEnrolled;

   return (
      <>
         <ExpansionList className="change-password-tab md-cell md-cell--8 center">
            <ExpansionPanel
               label="Multi-factor Authentication (MFA)"
               footer={null}
               expanded
               expanderIcon={<></>}
               onExpandToggle={FN_EMPTY_VOID}
               style={{ textAlign: 'left' }}
            >
               {/* Preferred method + last updated — side by side */}
               <Grid>
                  <Cell size={6}>
                     <span
                        style={{
                           fontSize: '12px',
                           color: '#757575',
                           display: 'block',
                        }}
                     >
                        Preferred Method
                     </span>
                     <strong>
                        {mfaDetails?.preferredMfaMethod ?? 'Not set'}
                     </strong>
                  </Cell>
                  <Cell size={6}>
                     <span
                        style={{
                           fontSize: '12px',
                           color: '#757575',
                           display: 'block',
                        }}
                     >
                        Last Updated
                     </span>
                     <strong>
                        {mfaDetails
                           ? new Date(mfaDetails.updatedDate).toLocaleString()
                           : '—'}
                     </strong>
                  </Cell>
               </Grid>

               {/* Method cards */}
               <Grid style={{ borderTop: '1px solid #e0e0e0' }}>
                  <Cell size={6}>
                     <Card style={{ height: '100%', position: 'relative' }}>
                        <CardTitle
                           title={
                              <span>
                                 SMS
                                 {mfaDetails?.preferredMfaMethod === 'SMS' && (
                                    <span
                                       style={{
                                          marginLeft: '8px',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          backgroundColor: '#26c6da',
                                          color: '#fff',
                                          borderRadius: '4px',
                                          padding: '2px 6px',
                                          verticalAlign: 'middle',
                                       }}
                                    >
                                       Default
                                    </span>
                                 )}
                              </span>
                           }
                           subtitle={
                              mfaDetails?.smsEnrolledDate
                                 ? `Enrolled: ${new Date(
                                      mfaDetails.smsEnrolledDate
                                   ).toLocaleDateString()}${
                                      contact.mobileNumber
                                         ? ` · ••••${contact.mobileNumber.slice(
                                              -4
                                           )}`
                                         : ''
                                   }`
                                 : 'Not enrolled'
                           }
                           avatar={<FontIcon>sms</FontIcon>}
                        />
                        {mfaDetails?.smsEnrolledDate && (
                           <div
                              style={{
                                 padding: '0 16px 16px',
                                 display: 'flex',
                                 gap: '8px',
                                 justifyContent: 'space-between',
                              }}
                           >
                              <Button
                                 flat
                                 style={{
                                    color: smsDefaultDisabled ? '#fff' : '#000',
                                    padding: '0 16px',
                                    height: '36px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                 }}
                                 disabled={smsDefaultDisabled}
                                 onClick={(): void => {
                                    dispatch(
                                       IdentityActions.patchMfaDefaultMethodRequest(
                                          contact.userId,
                                          'SMS'
                                       )
                                    );
                                 }}
                              >
                                 <FontIcon
                                    style={{
                                       fontSize: '16px',
                                       marginRight: '6px',
                                       color: smsDefaultDisabled
                                          ? '#fff'
                                          : '#000',
                                    }}
                                 >
                                    star
                                 </FontIcon>
                                 Set as Default
                              </Button>
                              <Button
                                 flat
                                 disabled={smsRemoveDisabled}
                                 style={{
                                    color: smsRemoveDisabled ? '#fff' : '#000',
                                    padding: '0 16px',
                                    height: '36px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                 }}
                                 onClick={(): void => openDialog('remove-sms')}
                              >
                                 <FontIcon
                                    style={{
                                       fontSize: '16px',
                                       marginRight: '4px',
                                       color: smsRemoveDisabled
                                          ? '#fff'
                                          : '#000',
                                    }}
                                 >
                                    delete
                                 </FontIcon>
                                 Remove
                              </Button>
                           </div>
                        )}
                     </Card>
                  </Cell>
                  <Cell size={6}>
                     <Card style={{ height: '100%', position: 'relative' }}>
                        <CardTitle
                           title={
                              <span>
                                 Authenticator
                                 {mfaDetails?.preferredMfaMethod ===
                                    'AUTHENTICATOR' && (
                                    <span
                                       style={{
                                          marginLeft: '8px',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          backgroundColor: '#26c6da',
                                          color: '#fff',
                                          borderRadius: '4px',
                                          padding: '2px 6px',
                                          verticalAlign: 'middle',
                                       }}
                                    >
                                       Default
                                    </span>
                                 )}
                              </span>
                           }
                           subtitle={
                              mfaDetails?.totpEnrolledDate
                                 ? `Enrolled: ${new Date(
                                      mfaDetails.totpEnrolledDate
                                   ).toLocaleDateString()}`
                                 : 'Not enrolled'
                           }
                           avatar={<FontIcon>security</FontIcon>}
                        />
                        {mfaDetails?.totpEnrolledDate && (
                           <div
                              style={{
                                 padding: '0 16px 16px',
                                 display: 'flex',
                                 gap: '8px',
                                 justifyContent: 'space-between',
                              }}
                           >
                              <Button
                                 flat
                                 style={{
                                    color: authenticatorDefaultDisabled
                                       ? '#fff'
                                       : '#000',
                                    padding: '0 16px',
                                    height: '36px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                 }}
                                 disabled={authenticatorDefaultDisabled}
                                 onClick={(): void => {
                                    dispatch(
                                       IdentityActions.patchMfaDefaultMethodRequest(
                                          contact.userId,
                                          'AUTHENTICATOR'
                                       )
                                    );
                                 }}
                              >
                                 <FontIcon
                                    style={{
                                       fontSize: '16px',
                                       marginRight: '6px',
                                       color: authenticatorDefaultDisabled
                                          ? '#fff'
                                          : '#000',
                                    }}
                                 >
                                    star
                                 </FontIcon>
                                 Set as Default
                              </Button>
                              <Button
                                 flat
                                 disabled={authenticatorRemoveDisabled}
                                 style={{
                                    color: authenticatorRemoveDisabled
                                       ? '#fff'
                                       : '#000',
                                    padding: '0 16px',
                                    height: '36px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                 }}
                                 onClick={(): void =>
                                    openDialog('remove-authenticator')
                                 }
                              >
                                 <FontIcon
                                    style={{
                                       fontSize: '16px',
                                       marginRight: '4px',
                                       color: authenticatorRemoveDisabled
                                          ? '#fff'
                                          : '#000',
                                    }}
                                 >
                                    delete
                                 </FontIcon>
                                 Remove
                              </Button>
                           </div>
                        )}
                     </Card>
                  </Cell>
               </Grid>

               {/* Reset MFA: left title/description, right button */}
               <Grid
                  style={{
                     alignItems: 'center',
                     borderTop: '1px solid #e0e0e0',
                  }}
               >
                  <Cell size={6}>
                     <strong>Reset MFA</strong>
                     <p
                        style={{
                           margin: '4px 0 0',
                           color: '#757575',
                           fontSize: '14px',
                        }}
                     >
                        Removes all enrolled methods. The user will need to
                        re-enroll at next login.
                     </p>
                  </Cell>
                  <Cell
                     size={6}
                     style={{ display: 'flex', justifyContent: 'flex-end' }}
                  >
                     <Button
                        raised
                        disabled={resetDisabled}
                        style={{
                           backgroundColor: resetDisabled
                              ? undefined
                              : '#f57c00',
                           color: '#fff',
                           padding: '0 16px',
                           height: '36px',
                           display: 'inline-flex',
                           alignItems: 'center',
                        }}
                        onClick={(): void => openDialog('reset-mfa')}
                     >
                        <FontIcon
                           style={{
                              fontSize: '16px',
                              marginRight: '6px',
                              color: '#fff',
                           }}
                        >
                           restore
                        </FontIcon>
                        Reset MFA
                     </Button>
                  </Cell>
               </Grid>

               {notice && (
                  <>
                     <hr />
                     <Grid>
                        <Cell size={12}>
                           <h3>THANK YOU!</h3>
                           <h5>{notice}</h5>
                        </Cell>
                     </Grid>
                  </>
               )}
            </ExpansionPanel>
         </ExpansionList>

         <DialogContainer
            id="mfa-confirm-dialog"
            visible={showDialog}
            title={pendingAction ? DIALOG_TITLES[pendingAction] : ''}
            width={500}
            portal
            onHide={FN_EMPTY_VOID}
            contentStyle={{ padding: '0 24px 24px' }}
            footerStyle={{ padding: '0 24px 24px 24px' }}
            actions={[
               <Button
                  key="cancel"
                  flat
                  style={{ color: '#039be5', padding: '0 16px' }}
                  onClick={closeDialog}
               >
                  Cancel
               </Button>,
               <Button
                  key="confirm"
                  flat
                  style={{ color: '#f57c00', padding: '0 16px' }}
                  onClick={handleConfirm}
               >
                  Confirm
               </Button>,
            ]}
         >
            <span>{pendingAction ? DIALOG_MESSAGES[pendingAction] : ''}</span>
         </DialogContainer>
      </>
   );
};

export default MfaTab;
